import { Variants, Transition } from 'framer-motion'

// ============================================
// CORE TIMING & EASING
// ============================================

// Consistent easing curve - smooth expo-out feel
const ease = [0.25, 0.1, 0.25, 1] // cubic-bezier for smooth deceleration
const easeOut = [0, 0, 0.2, 1]    // quick start, smooth end

// Standard durations
const duration = {
  fast: 0.2,
  normal: 0.35,
  slow: 0.5,
}

// Standard spring for interactive elements (available for future use)
// const springConfig = {
//   type: 'spring' as const,
//   stiffness: 300,
//   damping: 30,
// }

// ============================================
// PAGE CONTAINER - Use on page wrapper
// ============================================

export const pageContainer: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: duration.fast,
      when: 'beforeChildren',
      staggerChildren: 0.06,
    },
  },
}

// ============================================
// CONTENT ANIMATIONS - Use on sections/cards
// ============================================

export const fadeInUp: Variants = {
  hidden: {
    opacity: 0,
    y: 16,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: duration.normal,
      ease: ease,
    },
  },
}

export const fadeIn: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: duration.normal,
      ease: easeOut,
    },
  },
}

export const scaleIn: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.96,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: duration.normal,
      ease: ease,
    },
  },
}

// ============================================
// STAGGER CONTAINERS
// ============================================

export const staggerContainer: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.1,
    },
  },
}

export const staggerList: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.04,
    },
  },
}

// ============================================
// LIST ITEM ANIMATIONS
// ============================================

export const listItem: Variants = {
  hidden: {
    opacity: 0,
    y: 8,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: duration.fast,
      ease: easeOut,
    },
  },
}

// ============================================
// CARD ANIMATIONS - For room cards, stat cards etc
// ============================================

export const cardReveal: Variants = {
  hidden: {
    opacity: 0,
    y: 12,
    scale: 0.98,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: duration.normal,
      ease: ease,
    },
  },
}

// ============================================
// POKER/GAME SPECIFIC
// ============================================

export const cardDeal: Variants = {
  hidden: {
    opacity: 0,
    y: -20,
    rotateZ: -5,
    scale: 0.9,
  },
  visible: (i: number = 0) => ({
    opacity: 1,
    y: 0,
    rotateZ: 0,
    scale: 1,
    transition: {
      duration: duration.normal,
      delay: i * 0.08,
      ease: ease,
    },
  }),
}

export const chipSlide: Variants = {
  hidden: {
    opacity: 0,
    y: -12,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: duration.fast,
      ease: easeOut,
    },
  },
}

// ============================================
// INTERACTIVE STATES - For buttons, cards
// ============================================

// Subtle hover lift for cards
export const hoverLift = {
  y: -2,
  transition: { duration: 0.2, ease: easeOut },
}

// Tap scale for buttons
export const tapScale = {
  scale: 0.97,
  transition: { duration: 0.1 },
}

// Subtle hover scale
export const hoverScale = {
  scale: 1.02,
  transition: { duration: 0.2, ease: easeOut },
}

// ============================================
// MODAL / OVERLAY
// ============================================

export const modalBackdrop: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: duration.fast },
  },
  exit: {
    opacity: 0,
    transition: { duration: duration.fast, delay: 0.1 },
  },
}

export const modalContent: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.96,
    y: 8,
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: duration.normal,
      ease: ease,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.98,
    transition: { duration: duration.fast },
  },
}

// ============================================
// UTILITY VARIANTS
// ============================================

export const slideInRight: Variants = {
  hidden: { opacity: 0, x: 20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: duration.normal,
      ease: ease,
    },
  },
}

export const slideInLeft: Variants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: duration.normal,
      ease: ease,
    },
  },
}

export const slideUp: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: duration.normal,
      ease: ease,
    },
  },
  exit: {
    opacity: 0,
    y: 16,
    transition: { duration: duration.fast },
  },
}

// ============================================
// SHARED TRANSITION CONFIGS
// ============================================

export const smoothTransition: Transition = {
  duration: duration.normal,
  ease: ease,
}

export const quickTransition: Transition = {
  duration: duration.fast,
  ease: easeOut,
}

// For layout animations
export const layoutTransition: Transition = {
  type: 'spring',
  stiffness: 400,
  damping: 35,
}
